import { FC } from 'react';
import { AlertTriangle, Building2, PhoneCall, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Separator } from '@/components/ui/separator';
import { formatPhone, normalizePhone } from '@/utils/phone';

interface Props {
  companyName: string;
  companyEmail: string;
  companyPhone: string;
  companyLogo?: string | null;
  // Flow-specific copy (already interpolated with the company name).
  subtitle: string;
  description: string;
  reference: string;
}

const PaymentUnavailableScreen: FC<Props> = ({
  companyName,
  companyEmail,
  companyPhone,
  companyLogo,
  subtitle,
  description,
  reference,
}) => {
  const { t } = useTranslation(['checkout']);

  return (
    <div className="flex min-h-[80vh] items-center justify-center px-4 py-10">
      <div className="relative w-full max-w-xl overflow-hidden rounded-2xl border border-amber-200 bg-white shadow-xl">
        <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-amber-400 via-orange-500 to-rose-500" />

        <div className="flex flex-col items-center px-6 pt-10 pb-6 text-center sm:px-10">
          <div className="relative mb-6 flex size-20 items-center justify-center">
            <div className="absolute inset-0 animate-pulse rounded-full bg-amber-200/60" />
            <div className="relative flex size-20 items-center justify-center rounded-full bg-gradient-to-br from-amber-100 to-orange-200 ring-8 ring-amber-50">
              <AlertTriangle className="size-10 text-amber-600" strokeWidth={2.25} />
            </div>
          </div>

          <h1 className="mb-2 text-2xl font-bold text-gray-900 sm:text-3xl">
            {t('checkout:payment-unavailable-title', 'Payment Unavailable')}
          </h1>
          <p className="max-w-md text-sm text-gray-600 sm:text-base">{subtitle}</p>
        </div>

        <Separator />

        <div className="bg-gradient-to-b from-amber-50/50 to-white px-6 py-6 sm:px-10">
          <div className="mb-4 flex items-center gap-3">
            {companyLogo ? (
              <img
                src={companyLogo}
                alt={companyName}
                className="size-12 rounded-lg border border-gray-200 object-contain bg-white p-1"
              />
            ) : (
              <div className="flex size-12 items-center justify-center rounded-lg bg-gradient-to-br from-gray-100 to-gray-200">
                <Building2 className="size-6 text-gray-600" />
              </div>
            )}
            <div className="text-left">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                {t('checkout:payment-unavailable-contact-label', 'Please contact')}
              </p>
              <p className="text-base font-semibold text-gray-900">{companyName}</p>
            </div>
          </div>

          <div className="rounded-xl border border-amber-100 bg-amber-50/70 p-4">
            <p className="text-sm leading-relaxed text-amber-900">{description}</p>
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-xs text-amber-800">
              {companyPhone && (
                <a href={`tel:${normalizePhone(companyPhone)}`} className="inline-flex items-center gap-1.5 hover:underline">
                  <PhoneCall className="size-3.5" />
                  {formatPhone(companyPhone)}
                </a>
              )}
              {companyEmail && (
                <a href={`mailto:${companyEmail}`} className="inline-flex items-center gap-1.5 hover:underline">
                  <Mail className="size-3.5" />
                  {companyEmail}
                </a>
              )}
            </div>
          </div>

          <p className="mt-5 text-center text-xs text-gray-500">{reference}</p>
        </div>
      </div>
    </div>
  );
};

export default PaymentUnavailableScreen;
